// Log levels in order of severity
const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Get the log level from environment variable or default to INFO
function getInitialLogLevel() {
  const envLogLevel = import.meta.env.VITE_LOG_LEVEL;
  if (!envLogLevel) return LogLevel.INFO;
  
  switch (envLogLevel.toUpperCase()) {
    case 'ERROR': return LogLevel.ERROR;
    case 'WARN': return LogLevel.WARN;
    case 'INFO': return LogLevel.INFO;
    case 'DEBUG': return LogLevel.DEBUG;
    default: return LogLevel.INFO;
  }
}

// Current log level - initialized from environment variable
let currentLogLevel = getInitialLogLevel();

// Whether logging is enabled
let isLoggingEnabled = true;

// List of sensitive fields to redact
const sensitiveFields = [
  'token',
  'password',
  'idToken',
  'jwtToken',
  'accessToken',
  'secret',
  'key'
];

// Function to redact sensitive information from objects
function redactSensitiveData(obj) {
  if (!obj) return obj;
  
  const redacted = { ...obj };
  for (const key in redacted) {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
      redacted[key] = '[REDACTED]';
    } else if (typeof redacted[key] === 'object') {
      redacted[key] = redactSensitiveData(redacted[key]);
    }
  }
  return redacted;
}

// Main logging function
function log(level, message, data = null) {
  if (!isLoggingEnabled || level > currentLogLevel) return;

  const timestamp = new Date().toISOString();
  const levelName = Object.keys(LogLevel).find(key => LogLevel[key] === level);
  
  let logMessage = `[${timestamp}] ${levelName}: ${message}`;
  
  if (data) {
    const redactedData = redactSensitiveData(data);
    logMessage += `\n${JSON.stringify(redactedData, null, 2)}`;
  }
  
  switch (level) {
    case LogLevel.ERROR:
      console.error(logMessage);
      break;
    case LogLevel.WARN:
      console.warn(logMessage);
      break;
    case LogLevel.INFO:
      console.info(logMessage);
      break;
    case LogLevel.DEBUG:
      console.debug(logMessage);
      break;
  }
}

// Function to get current log level name
function getCurrentLogLevelName() {
  return Object.keys(LogLevel).find(key => LogLevel[key] === currentLogLevel) || 'UNKNOWN';
}

// Public API
export const Logger = {
  // Log level constants
  LogLevel,
  
  // Expose current log level
  get currentLogLevel() {
    return currentLogLevel;
  },
  
  // Get current log level name
  get currentLogLevelName() {
    return getCurrentLogLevelName();
  },
  
  // Configuration methods
  setLogLevel(level) {
    currentLogLevel = level;
  },
  
  enable() {
    isLoggingEnabled = true;
  },
  
  disable() {
    isLoggingEnabled = false;
  },
  
  isEnabled() {
    return isLoggingEnabled;
  },
  
  // Logging methods
  error(message, data) {
    log(LogLevel.ERROR, message, data);
  },
  
  warn(message, data) {
    log(LogLevel.WARN, message, data);
  },
  
  info(message, data) {
    log(LogLevel.INFO, message, data);
  },
  
  debug(message, data) {
    log(LogLevel.DEBUG, message, data);
  }
};

// Vue Plugin for Logger
export const LoggerPlugin = {
  install(app) {
    // Make Logger available globally in all components via this.$logger
    app.config.globalProperties.$logger = Logger;
    
    // Also provide Logger as a composable via inject
    app.provide('logger', Logger);
  }
}; 